<template>
    <div class="pathways-mapping mb-5">
        <div class="row px-2 " v-if="(local_census_connection.id == null || local_census_connection.id <= 0) && !isFile() ">
            <div class="card mb-3">
                <div class="card-body">
                    <h3>Information</h3>
                    <p>Before data can be sent to {{ local_destination.integration.name }}, you first need to align the columns to those available in this data source and decide how the data should be kept in sync on an ongoing basis. <span v-if="this.census_connection.source == 'data_blend'">If there is information missing from the data source, <a :href="'/integrations/'+client.url+'/mapping/data_set/'+data_set_id +'?step=3'">you can click here to map additional columns to that source</a>.</span></p>
                    <div class="row">
                        <div class="col-md-6 col-12">
                            <label>Destination Object</label>
                            <select class="form-select"  :value="effectiveDestinationObject" @change="onDestinationObjectChange($event.target.value)"
                            :disabled="local_destination.census_destination_objects.length==1 || local_census_connection.id > 0 || !canEdit('pathways')"
                                :class="{'is-invalid' : errors.destination_object != ''}" aria-label="Destination Object">
                                <option v-for="obj in local_destination.census_destination_objects" :value="obj.full_name" v-if="obj.data == null">{{obj.label}}</option>
                            </select>
                            <div class="text-danger invalid-feedback" v-if="errors.destination_object != ''">
                                {{errors.destination_object}}
                            </div>
                            <div v-if="showConversionActionPicker" class="mt-2">
                                <label class="form-label">Conversion Action</label>
                                <select class="form-select" :value="selectedConversionActionId" @change="onConversionActionChange($event.target.value)"
                                    :disabled="local_census_connection.id > 0 || !canEdit('pathways')"
                                    :class="{'is-invalid' : errors.conversion_action != ''}" aria-label="Conversion Action">
                                    <option value="">Select a conversion action</option>
                                    <option v-for="ca in conversionActions" :key="ca.id" :value="ca.id">{{ ca.name }}</option>
                                </select>
                                <div class="text-danger invalid-feedback" v-if="errors.conversion_action != ''">
                                    {{errors.conversion_action}}
                                </div>
                            </div>
                        </div>
                        <div v-if="operations.length > 1" class="col-md-6 col-12 mt-2 mt-md-0">
                            <label>Method of Sync</label>
                            <select class="form-select" v-model="local_census_connection.operation"
                                :class="{'is-invalid' : errors.operation != ''}"
                                :disabled="local_census_connection.id > 0 || !canEdit('pathways') " aria-label="Method of Sync">
                                <option v-for="ops in operations" :value="ops" >{{getOperationExplanation(ops)}}</option>
                            </select>
                            <div class="text-danger invalid-feedback" v-if="errors.operation != ''">
                                {{errors.operation}}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <div class="row px-2" v-if="segment != null">
            <div class="card">
                <div class="card-body">
                    <div v-if="local_census_connection.filter_description">
                        <h4 class="mb-2">Filters Applied:</h4>
                        <div class="d-flex align-items-start mb-2">
                            <input type="checkbox" class="form-check-input mt-1 me-2" disabled checked  v-if="census_connection.filter_description">
                            <div>
                                <strong  v-if="local_census_connection.filter_description">Audience Definition:</strong>
                                <span v-html="segment.description" class="mb-0"></span>
                            </div>
                        </div>
                        <div class="d-flex align-items-start" v-if="local_census_connection.filter_description">
                            <input type="checkbox" class="form-check-input mt-1 me-2" id="local_not_ignore_filter" 
                                v-model="local_not_ignore_filter"
                                :disabled="!canEdit('pathways')">
                            <label for="local_not_ignore_filter">
                                <strong>Default Pathway Filter:</strong>
                                <span v-html="local_census_connection.filter_description" class="mb-0"></span>
                            </label>
                        </div>
                    </div>
                    <div v-else>
                        <h4 class="mb-2">Segment Definition:</h4>
                        <p v-html="segment.description" class="mb-0"></p>
                    </div>
                </div>
            </div>
        </div>

        <div class="row px-2 mb-3" v-if="local_census_connection.destination_object != null && local_census_connection.operation && !isFile() ">
            <div class="card">
                <div class="card-body">
                    <pathways-column-mapping :client="client" :source="local_census_connection"
                    :destination="local_destination" :dest_columns="destination_fields" @refresh="refreshColumns()" @prioritize="updatePrioritization"
                    :source_columns="source_fields" v-model="local_census_connection.mappings"
                    :if_allow_edit="canEdit('pathways')"
                    ref="column_mapping"></pathways-column-mapping>

                    <button v-if="local_census_connection.destination_object != null && !isFile() && canEdit('pathways')" class="btn btn-secondary mt-3" @click="addColumnMapping()"  :disabled="busy==true">
                        <i class="fas fa-plus"></i> Add Column Mapping
                    </button>
                    <div v-else-if="local_census_connection.destination_object != null && !isFile() && !canEdit('pathways')" class="d-inline-block" data-bs-toggle="tooltip" title="You do not have privileges to complete this action">
                        <button class="btn btn-secondary mt-3" :disabled="true">
                            <i class="fas fa-plus"></i> Add Column Mapping
                        </button>
                    </div>
                </div>
            </div>
        </div>
        <div class="row px-2 mb-3" v-else-if="isFile() ">
            <div class="card">
                <div class="card-body">
                    <file-column-mapping :client="client" :source="local_census_connection"
                    :source_columns="source_fields" :destination="local_destination"
                    v-model="local_census_connection.mappings" @prioritize="updatePrioritization"
                    ref="file_mapping"
                    :if_allow_edit="canEdit('pathways')"></file-column-mapping>
                </div>
            </div>
        </div>

        <div class="alert alert-danger mb-3" v-if="errors.form">
            There was an issue saving this pathway mapping. Check the fields above to get more information.
        </div>

        <div class="row">
            <!-- Save Button -->
            <div class="mb-3 d-flex flex-row gap-2">
                <button v-if="canEdit('pathways')" type="submit" class="btn btn-primary save-button" @click="save()" :disabled="busy==true">
                    <span v-if="busy">
                        <div class="spinner-border  spinner-border-sm float-left" role="status"> <span class="visually-hidden">Saving...</span></div> 
                        Saving
                    </span>
                    <span v-else>
                        <img class="icon" src="/img/icons/avid-icons/save.svg"> Save
                    </span>
                </button>
                <div v-else class="d-inline-block" data-bs-toggle="tooltip" title="You do not have privileges to complete this action">
                    <button class="btn btn-secondary save-button" :disabled="true">
                        <img class="icon" src="/img/icons/avid-icons/save.svg"> Save
                    </button>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import { defineComponent, ref } from 'vue';
import Swal from 'sweetalert2';
import { router } from '@inertiajs/vue3';
import permissionsMixin from '../../mixins/permissions';

export default defineComponent({
    mixins: [permissionsMixin],
    name: 'PathwaysMapping',
    props: {
        client: {
            type: Object,
            required: true
        },
        destination: {
            type: Object,
            required: true
        },
        census_connection: {
            type: Object,
            required: true
        },
        source_fields: {
            type: Array,
            required: true
        },
        segment: {
            type: Object,
            required: false,
            default: null
        }
    },
    data() {
        return {
            local_census_connection: ref(null),
            local_destination: null,
            operations: [],
            destination_fields: [],
            local_not_ignore_filter: false,
            errors:{
                destination_object: "",
                operation: "",
                conversion_action: "",
                form: false
            },
            conversionActions: [],
            busy: false,
            obj: {}
        };
    },
    beforeMount() {
        this.local_census_connection = JSON.parse(JSON.stringify(this.census_connection));
        // Ensure mappings is initialized as an array if it doesn't exist
        if (!this.local_census_connection.mappings) {
            this.local_census_connection.mappings = [];
        }
        this.local_destination = this.destination;
        this.local_not_ignore_filter = !this.ignore_filter;

        //Set the column options if the destination has been saved already
        if(this.local_census_connection.destination_object != null && !this.isFile()){
            const effectiveObj = this.getEffectiveDestinationObject(this.local_census_connection.destination_object);
            for(let i = 0; i < this.destination.census_destination_objects.length; i++) {
                const obj = this.destination.census_destination_objects[i];
                if(obj.full_name == effectiveObj){
                    this.operations = obj.supported_operations;
                    this.destination_fields = obj.fields;
                    break;
                }
            }
        }
        //If there is only one option
        else if(!this.isFile() && this.destination.census_destination_objects.length == 1){
            this.local_census_connection.destination_object = this.local_destination.census_destination_objects[0].full_name;
            this.destination_fields = this.destination.census_destination_objects[0].fields;
            this.operations = this.destination.census_destination_objects[0].supported_operations;
            this.changeDestination();
        }

        if(!this.isFile() && (this.local_destination.census_destination_objects == null || this.local_destination.census_destination_objects.length == 0)) {
            this.getDestinationObjects();
        }
        if (this.isGoogleAdsEnhancedConversion()) {
            this.fetchConversionActions();
        }
    },
    computed: {
        effectiveDestinationObject() {
            const obj = this.local_census_connection?.destination_object;
            if (!obj) return obj;
            if (obj === 'user_data' || obj === 'enhanced_conversion') return obj;
            try {
                const parsed = typeof obj === 'string' ? JSON.parse(obj) : obj;
                if (parsed && (parsed.conversion_action_id || parsed.conversionActionId)) {
                    return 'enhanced_conversion';
                }
            } catch (_) {}
            return obj;
        },
        showConversionActionPicker() {
            return this.isGoogleAdsEnhancedConversion();
        },
        selectedConversionActionId() {
            const obj = this.local_census_connection?.destination_object;
            if (!obj) return '';
            try {
                const parsed = typeof obj === 'string' ? JSON.parse(obj) : obj;
                return parsed?.conversion_action_id || parsed?.conversionActionId || '';
            } catch (_) {
                return '';
            }
        },
        data_set_id() {
            if(this.census_connection.source == 'data_blend') {
                //Loop through the primary data sets and find the one with source_subtype of constituents
                for(let i = 0; i < this.client.primary_data_sets.length; i++) {
                    if(this.client.primary_data_sets[i].source_subtype == 'constituents')
                        return this.client.primary_data_sets[i].id;
                }
            }

            return this.census_connection.data_source_id;
        }
    },
    methods: {
        isFile() {
            return this.local_destination.integration_id == "71b0c3b0-8390-49ec-9984-1c04b7384d38";
        },
        isGoogleAdsEnhancedConversion() {
            return this.local_destination?.integration?.display_name === 'Google Ads' &&
                this.effectiveDestinationObject === 'enhanced_conversion';
        },
        onDestinationObjectChange(value) {
            if (value === 'enhanced_conversion') {
                this.local_census_connection.destination_object = 'enhanced_conversion';
            } else {
                this.local_census_connection.destination_object = value;
            }
            this.errors.conversion_action = '';
            this.changeDestination();
            if (value === 'enhanced_conversion') {
                this.fetchConversionActions();
            }
        },
        onConversionActionChange(value) {
            if (!value) {
                this.local_census_connection.destination_object = 'enhanced_conversion';
            } else {
                this.local_census_connection.destination_object = JSON.stringify({ conversion_action_id: value });
            }
            this.errors.conversion_action = '';
        },
        fetchConversionActions() {
            if (!this.local_destination?.id) return;
            window.axios.post('/api/census/get_dropdown_options', {
                connection_id: this.local_destination.id,
                column: 'enhanced_conversion.conversion_action'
            }).then(response => {
                this.conversionActions = response.data?.objects || [];
            }).catch(() => {
                this.conversionActions = [];
            });
        },
        getEffectiveDestinationObject(obj) {
            if (!obj) return obj;
            if (obj === 'user_data' || obj === 'enhanced_conversion') return obj;
            try {
                const parsed = typeof obj === 'string' ? JSON.parse(obj) : obj;
                if (parsed && (parsed.conversion_action_id || parsed.conversionActionId)) {
                    return 'enhanced_conversion';
                }
            } catch (_) {}
            return obj;
        },
        getOperationExplanation(ops) {
            if(ops == 'mirror')
                return "Mirror - Records will added, updated, and removed to keep the sources in full sync"
            if(ops == 'append')
                return 'Append - New records will be added to the destination but existing will not be updated or removed';
            if(ops == 'upsert')
                return "Update or Create - Update existing destination records when IDs match, otherwise create new ones if they're missing"
            if(ops == 'update')
                return "Update Only - Matching records will be updated but nothing will be added or removed"
            if(ops == 'delete')
                return "Delete - Delete syncs takes the list of provided IDs and deletes them from the destination. Be careful! Providing an incorrect set of IDs can result in data loss!"
            if(ops == 'create' || ops == 'insert')
                return "Create Only - Create a new object if it doesn't exist in the destination"
        },
        changeDestination() {
            const effectiveObj = this.getEffectiveDestinationObject(this.local_census_connection.destination_object);
            for(var i = 0; i < this.local_destination.census_destination_objects.length; i++) {
                var obj = this.local_destination.census_destination_objects[i];
                if(obj.full_name == effectiveObj){
                    this.operations = obj.supported_operations;
                    this.destination_fields = obj.fields;
                    break;
                }
            }

            //Now update the operation on the local census connection
            if(this.operations == null){
                this.local_census_connection.operation = null;
                this.operations = [];
                this.destination_fields = [];
            }

            //Set the operation to the first one if it isn't already set
            else if(this.local_census_connection.operation == null){
                this.local_census_connection.operation = this.operations[0];
            }
            //If this.local_census_connection.operation is set and not in the operation array
            else if(this.local_census_connection.operation != null && !this.operations.includes(this.local_census_connection.operation)) {
                this.local_census_connection.operation = this.operations[0];
            }

            //Update the pathway mapping vue to remove columns that don't exist once they select a destination
            if(this.local_census_connection.destination_object != null && this.local_census_connection.mappings != null) {
                for(var i = this.local_census_connection.mappings.length-1; i >= 0; i--) {
                    let found = false;
                    for(var j = 0; j < this.destination_fields.length; j++) {
                    //If the [j].full_name is not in any of the destination columns
                        if(this.destination_fields[j].full_name == this.local_census_connection.mappings[i].destination_column) {
                            found = true;
                            break;
                        }
                    }
                    //If I didn't find the mapping, remove it
                    if(!found)
                        this.local_census_connection.mappings.splice(i, 1);
                }
            }

            if(this.$refs.column_mapping != null)
                this.$refs.column_mapping.resetMapping();
        },
        updatePrioritization(priority) {
            this.local_census_connection.prioritization_column = priority;
        },
        getDestinationObjects() {
            var form_data = {
                client_id: this.client.id,
                connection_id: this.local_destination.id
            }
            window.axios.post('/api/census/get_destination_objects', form_data)
            .then(response => {
                //Now I need to set a timeout to check the destination to see when it changes
                if(response.data.objects == null || response.data.objects.length == 0)
                    setTimeout(this.getDestinationObjects, 500);
                else
                    this.local_destination.census_destination_objects = response.data.objects;

            });
        },
        refreshColumns() {
            var form_data = {
                client_id: this.client.id,
                connection_id: this.local_destination.id,
                destination_object: this.local_census_connection.destination_object
            }
            window.axios.post('/api/census/refresh_destination', form_data)
            .then(response => {
                //Now I need to set a timeout to check the destination to see when it changes
                Swal.fire({
                    title: "Processing...",
                    html: "The refresh process has begun. Once it has finished, the columns options below will be updated.",
                    customClass: {
                        popup: 'swal-warning'
                    },
                }).then((result) => {

                    //Now I need to set a timeout to check the destination to see when it changes
                    setTimeout(function(scope) {
                        window.axios.get('/api/connection/' + scope.local_destination.id).then(response => {
                            if(response.data.connection != scope.local_destination){
                                scope.local_destination = response.data.connection;
                                const effectiveObj = scope.getEffectiveDestinationObject(scope.local_census_connection.destination_object);
                                for(var i = 0; i < scope.local_destination.census_destination_objects.length; i++) {
                                    var obj = scope.local_destination.census_destination_objects[i];
                                    if(obj.full_name == effectiveObj){
                                        scope.destination_fields = obj.fields;
                                        break;
                                    }
                                }
                            }
                        });
                    }, 5000, this);
                });
            });
        },
        validateCensusConnection() {
            this.errors.operation = "";
            this.errors.destination_object = "";
            this.errors.conversion_action = "";

            let is_valid = true;

            if(this.local_census_connection.operation == null || this.local_census_connection.operation == ''){
                this.errors.operation = 'The method of sync is a required field';
                is_valid = false;
            }
            if(this.local_census_connection.destination_object == null || this.local_census_connection.destination_object == '') {
                is_valid = false;
                this.errors.destination_object = 'The destination object is a required field';
            }
            if (this.effectiveDestinationObject === 'enhanced_conversion' && this.local_destination?.integration?.display_name === 'Google Ads') {
                const caId = this.selectedConversionActionId;
                if (!caId) {
                    is_valid = false;
                    this.errors.conversion_action = 'Please select a conversion action';
                }
            }

            return is_valid;
        },
        addColumnMapping(){
            this.local_census_connection.mappings.push({
                id: -1 * Math.random(),
                client_id: this.client.id,
                census_connection_id: this.local_census_connection.id,
                source_column: this.getBlankSourceColumn(),
                destination_column: null,
                is_primary_identifier: false,
            });
        },
        getBlankSourceColumn() {
            return {
                main_category: 'Columns',
                category: null,
                type: 'string',
                value: null,
                text: ""
            };
        },
        getModelId() {
            let urlString = window.location.href;
            // Parse the URL
            const url = new URL(urlString);
            // Split the pathname part of the URL on '/', filter out empty segments
            const segments = url.pathname.split('/').filter(segment => segment !== '');
            // Get the seventh segment (the model ID) if there is one
            return segments.length == 7 ? segments[segments.length - 1] : null;
        },
        save() {
            var is_valid = true;
            if(this.isFile()){
                this.local_census_connection.operation = 'create';
                this.local_census_connection.destination_object = 'file';

                if(this.$refs.file_mapping != undefined && !this.$refs.file_mapping.isValid())
                    is_valid = false;
            }
            else {
                //Validate the form and the mapping
                is_valid = this.validateCensusConnection();
                if(this.$refs.column_mapping != undefined && !this.$refs.column_mapping.isValid())
                    is_valid = false;
            }

            if(!is_valid)
                this.errors.form = true;
            else {
                this.errors.form = false;
                var _self = this;
                this.busy = true;

                let model_id = this.getModelId();

                // If there is a segment and checkboxes are visible, update the sync first
                if (this.segment && this.local_census_connection.filter_description) {
                    window.axios.post('/api/census/update_sync', {
                        client_id: this.client.id,
                        segment_id: this.segment.id,
                        census_connection_id: this.local_census_connection.id,
                        ignore_filter: !this.local_not_ignore_filter
                    }).then(response => {
                        // Continue with the rest of the save process
                        _self.continueSave(model_id);
                    }).catch(error => {
                        this.busy = false;
                        const message = error.response?.data?.message;
                        
                        Swal.fire({
                            title: "Error!",
                            html: error.response?.data?.message || "There was an error updating the sync settings. Please try again.",
                            icon: "error",
                            customClass: {
                                popup: 'swal-error'
                            },
                        }).then((result) => {
                            if (
                                error.response?.status === 500 &&
                                typeof message === 'string' &&
                                message.startsWith('The sync has been deleted.')
                            ) {
                                const managePathwaysUrl = `/pathways/${this.client.url}/manage_pathways`;
                                window.location.href = managePathwaysUrl;
                                return;
                            }
                        });
                    });
                } else {
                    // If no segment or checkboxes, just continue with save
                    this.continueSave(model_id);
                }
            }
        },
        continueSave(model_id) {

            // Clean up mappings - convert object source to source.value 
            let cleaned_mappings = [];   
            if (this.local_census_connection.mappings) {
                this.local_census_connection.mappings.forEach(mapping => {
                    if (mapping.source_column && typeof mapping.source_column.value === 'object') {
                        mapping.source_column.value = mapping.source_column.value.value;
                    }
                    if (mapping.destination_column && typeof mapping.destination_column === 'object') {
                        mapping.destination_column = mapping.destination_column.value;
                    }
                    cleaned_mappings.push(mapping);
                });
            }

            let cleaned_prioritization_columns = [];
            if (this.local_census_connection.prioritization_column) {
                this.local_census_connection.prioritization_column.forEach(column => {
                    if (column.column && typeof column.column === 'object') {
                        column.column = column.column.value;
                    }
                    cleaned_prioritization_columns.push(column);
                });
            }
            
            this.local_census_connection.mappings = [...cleaned_mappings];
            this.local_census_connection.prioritization_column = [...cleaned_prioritization_columns];

            if(this.local_census_connection.id == null) {
                window.axios.post('/api/census_connection', this.local_census_connection)
                .then(response => {
                    this.busy = false;
                    Swal.fire({
                        title: "Success!",
                        html: "The columns have been successfully mapped. You'll be redirected to the Pathways page momentarily and can select the audience to keep in sync moving forward.",
                        icon: "success",
                        customClass: {
                            popup: 'swal-success'
                        },
                    }).then((result) => {
                        if (document.referrer && document.referrer.includes('pathways')) {
                            window.history.back();
                        } else {
                            router.visit("/pathways/"+this.client.url+"/push_audience");
                        }
                    });
                })
                .catch(error => {
                    this.busy = false;
                    Swal.fire({
                        title: "Error!",
                        html: error.response?.data?.message || "There was an error saving the connection. Please try again.",
                        icon: "error",
                        customClass: {
                            popup: 'swal-error'
                        },
                    });
                });
            }
            //If there URL has seven folders
            else if(model_id != null){
                //if it is a column mapping just for a model
                window.axios.put('/api/census_model/' + model_id, this.local_census_connection)
                .then(response => {
                    this.busy = false;
                    Swal.fire({
                        title: "Success!",
                        html: "Your settings have been successfully updated. You'll be redirected to the Pathways page momentarily to continue selecting audience to keep in sync.",
                        icon: "success",
                        customClass: {
                            popup: 'swal-success'
                        },
                    }).then((result) => {
                        //Redirect back to the previous page and refresh it
                        if (document.referrer && document.referrer.includes('pathways'))
                            window.location.href = document.referrer;
                        else
                            window.location = "/pathways/"+this.client.url+"/push_audience";
                    });
                })
                .catch(error => {
                    this.busy = false;
                    Swal.fire({
                        title: "Error!",
                        html: error.response?.data?.message || "There was an error updating the model. Please try again.",
                        icon: "error",
                        customClass: {
                            popup: 'swal-error'
                        },
                    });
                });
            }
            //Just update the old filter
            else {
                this.local_census_connection.save_mapping = 1;
                window.axios.put('/api/census_connection/' + this.local_census_connection.id, this.local_census_connection)
                .then(response => {
                    this.busy = false;
                    Swal.fire({
                        title: "Success!",
                        html: "Your settings have been successfully updated. You'll be redirected to the Pathways page momentarily to continue selecting audience to keep in sync.",
                        icon: "success",
                        customClass: {
                            popup: 'swal-success'
                        },
                    }).then((result) => {
                        if (document.referrer && document.referrer.includes('pathways')) {
                            window.history.back();
                        } else {
                            router.visit("/pathways/"+this.client.url+"/push_audience");
                        }
                    });
                })
                .catch(error => {
                    this.busy = false;
                    Swal.fire({
                        title: "Error!",
                        html: error.response?.data?.message || "There was an error updating the connection. Please try again.",
                        icon: "error",
                        customClass: {
                            popup: 'swal-error'
                        },
                    });
                });
            }
        }
    }
});
</script>
